import {
  computed,
  inject,
  Injectable,
  resource,
  ResourceLoaderParams,
  ResourceRef,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import {
  EOrderOrigin,
  PaymentMethod,
  IPaymentProvider,
  UserDto,
  IConfiguredDirectTicket,
} from '@web/dtos';
import { IApiResponse } from '@web/shared-types';
import { firstValueFrom } from 'rxjs';
import { AppConfigService } from '../../app-config.service';
import { PaymentProviderGateway } from './payment-provider.gateway';

@Injectable({
  providedIn: 'root',
})
export class PaymentProviderStore {
  private readonly configService: AppConfigService = inject(AppConfigService);

  private readonly gateway: PaymentProviderGateway =
    new PaymentProviderGateway();
  private readonly paymentMethodsRequest: WritableSignal<number> = signal(0);

  #paymentProviderResource: ResourceRef<
    IApiResponse<IPaymentProvider[]> | undefined
  > = resource({
    loader: (): Promise<IApiResponse<IPaymentProvider[]>> => {
      return firstValueFrom(this.gateway.list());
    },
  });
  #paymentMethodsResource: ResourceRef<
    IApiResponse<PaymentMethod[]> | undefined
  > = resource({
    params: this.paymentMethodsRequest,
    loader: ({
      params,
    }: ResourceLoaderParams<number>): Promise<
      IApiResponse<PaymentMethod[]> | undefined
    > => {
      return params
        ? firstValueFrom(this.gateway.getPaymentMethods())
        : Promise.resolve(undefined);
    },
  });

  readonly isLoading: Signal<boolean> = computed((): boolean =>
    this.#paymentProviderResource.isLoading()
  );
  readonly isLoadingPaymentMethods: Signal<boolean> = computed((): boolean =>
    this.#paymentMethodsResource.isLoading()
  );
  readonly paymentProviders: Signal<IPaymentProvider[]> = computed(
    (): IPaymentProvider[] =>
      this.#paymentProviderResource.hasValue()
        ? (this.#paymentProviderResource.value()?.data ?? [])
        : []
  );
  readonly paymentMethods: Signal<PaymentMethod[]> = computed(
    (): PaymentMethod[] =>
      this.#paymentMethodsResource.hasValue()
        ? (this.#paymentMethodsResource.value()?.data ?? [])
        : []
  );

  getThumbnailPath(id: string | undefined): string {
    return this.configService.get().general.cmsUrl + '/paymentProviders/' + id;
  }

  buy() {
    return this.gateway.buy();
  }

  getPaymentMethods(): void {
    this.paymentMethodsRequest.update(value => value + 1);
  }

  addPaymentMethods(
    origin: EOrderOrigin,
    userData?: UserDto,
    products: IConfiguredDirectTicket[] = [],
    orderId: string | undefined = undefined
  ) {
    return this.gateway.addPaymentMethods(origin, userData, products, orderId);
  }

  addGuestPaymentMethods(
    customerId: string,
    origin: EOrderOrigin,
    products: IConfiguredDirectTicket[] = [],
    orderId: string | undefined = undefined
  ) {
    return this.gateway.addGuestPaymentMethods(
      customerId,
      origin,
      products,
      orderId
    );
  }

  deletePaymentMethods(paymentMethodId: string) {
    return this.gateway.deletePaymentMethods(paymentMethodId);
  }

  reloadMethods(): void {
    this.#paymentMethodsResource.reload();
  }
}
